export enum ResearchStaffLevelEnum {
  RESEARCHER = "RESEARCHER",
  REVIEWER_MANAGER = "REVIEWER_MANAGER",
}

export enum ResearchStageEnum {
  INTAKE_QUEUE = "INTAKE_QUEUE",
  ABOVE_BOARD_CONVERSATION = "ABOVE_BOARD_CONVERSATION",
  SHOP_QUEUE = "SHOP_QUEUE",
  SHOP_IN_PROCESS = "SHOP_IN_PROCESS",
  RETRY_QUEUE = "RETRY_QUEUE",
  RETRY_IN_PROCESS = "RETRY_IN_PROCESS",
  READY_FOR_REVIEW = "READY_FOR_REVIEW",
  REQUIRES_FIX = "REQUIRES_FIX",
  COMPLETED = "COMPLETED",
}

export enum ResearchWorkTypeEnum {
  TIME_SENSITIVE_REQUEST = "TIME_SENSITIVE_REQUEST",
  SCHEDULED_SHOP_ROUND = "SCHEDULED_SHOP_ROUND",
}

export enum ResearchCareTypeEnum {
  AA = "AA",
  IL = "IL",
  AL = "AL",
  MC = "MC",
  SN = "SN",
}

export enum ResearchDemandStatusEnum {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  FULFILLED = "FULFILLED",
}

export enum ResearchClientProgressStatusEnum {
  ON_TRACK = "ON_TRACK",
  AT_RISK = "AT_RISK",
  DUE_TODAY = "DUE_TODAY",
  OVERDUE = "OVERDUE",
  COMPLETE = "COMPLETE",
}

export enum ResearchPriorCycleActionEnum {
  CREATE_NEW_CYCLE = "CREATE_NEW_CYCLE",
  FULFILL_WITH_PRIOR_COMP = "FULFILL_WITH_PRIOR_COMP",
  REOPEN_PRIOR_CYCLE = "REOPEN_PRIOR_CYCLE",
}

export enum ResearchIntakeExclusionDispositionEnum {
  CURRENT_SUBJECT = "EXCLUDED_CURRENT_SUBJECT",
  KNOWN_CLIENT_SUBJECT = "EXCLUDED_KNOWN_CLIENT_SUBJECT",
}

export enum ResearchAcquisitionPathEnum {
  ABOVE_BOARD = "ABOVE_BOARD",
  ABOVE_BOARD_AND_1_SHOP = "ABOVE_BOARD_AND_1_SHOP",
  ABOVE_BOARD_AND_2_SHOPS = "ABOVE_BOARD_AND_2_SHOPS",
}

export const RESEARCH_ACTION = {
  CLAIM: "CLAIM",
  UNCLAIM: "UNCLAIM",
  ASSIGN: "ASSIGN",
  SUBMIT_FOR_REVIEW: "SUBMIT_FOR_REVIEW",
  SEND_TO_SHOP: "SEND_TO_SHOP",
  SEND_TO_RETRY: "SEND_TO_RETRY",
  APPROVE: "APPROVE",
  REJECT: "REJECT",
  SEND_BACK_FOR_FIX: "SEND_BACK_FOR_FIX",
  REACTIVATE: "REACTIVATE",
  ADD_NOTE: "ADD_NOTE",
  LINK_COMP: "LINK_COMP",
  UNLINK_COMP: "UNLINK_COMP",
  EDIT_DEMAND: "EDIT_DEMAND",
  CANCEL_DEMAND: "CANCEL_DEMAND",
} as const;

export type ResearchAction =
  (typeof RESEARCH_ACTION)[keyof typeof RESEARCH_ACTION];

export interface IResearchEntityRef {
  id: string;
  name: string;
}

export interface IResearchPerson {
  id: string;
  firstName: string;
  lastName: string;
}

export interface IResearchReferenceData {
  clients: IResearchEntityRef[];
  staff: Array<IResearchPerson & { level: ResearchStaffLevelEnum }>;
}

export interface IResearchProperty {
  id: string;
  sequentialId: number | null;
  name: string;
  city: string;
  state: string;
}

export interface IResearchOrigin {
  deal: IResearchEntityRef;
  subjectProperty: IResearchEntityRef;
}

export interface IResearchDemand {
  id: string;
  client: IResearchEntityRef;
  workType: ResearchWorkTypeEnum;
  dueDate: string;
  careTypes: ResearchCareTypeEnum[];
  status: ResearchDemandStatusEnum;
  allowedActions: ResearchAction[];
  origins: IResearchOrigin[];
}

export interface IResearchClientShare {
  client: IResearchEntityRef;
  shared: boolean;
}

export interface IResearchWorkingComp {
  id: string;
  deleted: boolean;
  requiredClientShares: IResearchClientShare[];
}

export interface IResearchAdvisory {
  eventId: string;
  client: IResearchEntityRef;
}

export interface IResearchArchival {
  at: string;
  reason: string;
}

export interface IResearchWorkItem {
  id: string;
  version: number;
  property: IResearchProperty;
  stage: ResearchStageEnum;
  assignedTo: IResearchPerson | null;
  workingComp: IResearchWorkingComp | null;
  demands: IResearchDemand[];
  allowedActions: ResearchAction[];
  advisories: IResearchAdvisory[];
  archival: IResearchArchival | null;
}

export interface IResearchWorkItemRow {
  id: string;
  stage: ResearchStageEnum;
  property: IResearchProperty;
  assignedTo: IResearchPerson | null;
  clients: IResearchEntityRef[];
  workTypes: ResearchWorkTypeEnum[];
  careTypes: ResearchCareTypeEnum[];
  dueDate: string | null;
  overdue: boolean;
  daysInStage: number;
  lastActivityAt: string;
  hasAdvisories: boolean;
  archived: boolean;
  availableToMe: boolean;
}

export interface IResearchTransitionCount {
  fromStage: ResearchStageEnum;
  toStage: ResearchStageEnum;
  count: number;
}

export interface IResearchSearchResponse {
  objects: IResearchWorkItemRow[];
  totalCount: number;
  activeCount: number;
  counts: Partial<Record<ResearchStageEnum, number>>;
  transitions: IResearchTransitionCount[];
}

export interface IResearchSummary {
  counts: Partial<Record<ResearchStageEnum, number>>;
  transitions: IResearchTransitionCount[];
  totalActive?: number;
  activeCount?: number;
}

export interface IResearchWorkItemsRequest {
  stage?: ResearchStageEnum;
  assignedUserId?: string;
  clientCompanyId?: string;
  workType?: ResearchWorkTypeEnum;
  dueFrom?: string;
  dueTo?: string;
  activityFrom?: string;
  activityTo?: string;
  availableToMe?: boolean;
  priorParticipantUserId?: string;
  overdueOnly?: boolean;
  search?: string;
  page?: number;
  size?: number;
}

export type IResearchClientProgressRequest = Pick<
  IResearchWorkItemsRequest,
  "clientCompanyId" | "workType" | "dueFrom" | "dueTo" | "search"
>;

export interface IResearchClientProgressBucket {
  client: IResearchEntityRef;
  dueDate: string;
  workTypes: ResearchWorkTypeEnum[];
  total: number;
  started: number;
  completed: number;
  remaining: number;
  completionPercentage: number;
  counts: Partial<Record<ResearchStageEnum, number>>;
  pace: {
    workdaysRemaining: number;
    requiredPerWorkday: number | null;
    expectedByToday: number;
    status: ResearchClientProgressStatusEnum;
  };
}

export interface IResearchProductivityPerson {
  user: IResearchPerson & { level: ResearchStaffLevelEnum };
  research: number;
  review: number;
  starts: number;
}

export interface IResearchProductivityDay {
  date: string;
  people: IResearchProductivityPerson[];
}

export interface IResearchProductivityRequest {
  from?: string;
  to?: string;
}

export interface IResearchWorkEvent {
  id: string;
  eventType: string;
  fromStage: ResearchStageEnum | null;
  toStage: ResearchStageEnum | null;
  actor: IResearchPerson | null;
  assignedTo: IResearchPerson | null;
  compId: string | null;
  reason: string | null;
  occurredAt: string;
  exitedAt: string | null;
  duration: string | null;
}

export interface IResearchHistoryEntry {
  id: string;
  stage: ResearchStageEnum;
  demands: Array<{
    client: IResearchEntityRef;
    dueDate: string;
    workType: ResearchWorkTypeEnum;
  }>;
  acquisitionPath: ResearchAcquisitionPathEnum | null;
  events: IResearchWorkEvent[];
}

export type ResearchIntakeSelection =
  | { type: "SUBJECT"; subjectId: string }
  | { type: "COMPETITOR"; competitorId: string }
  | { type: "SINGLE_PROPERTY_OVERRIDE"; propertyId: string };

export interface IResearchIntakeRequest {
  clientCompanyId: string;
  workType: ResearchWorkTypeEnum;
  dueDate: string;
  selections: ResearchIntakeSelection[];
  priorCycleActions?: Record<string, ResearchPriorCycleActionEnum>;
}

export interface IResearchIntakePreview {
  candidates: Array<{
    property: IResearchEntityRef;
    activeWork: { stage: ResearchStageEnum } | null;
    careTypes: ResearchCareTypeEnum[];
    recentComp: {
      verificationDate: string;
      ageDays: number;
      allowedActions: ResearchPriorCycleActionEnum[];
    } | null;
  }>;
  exclusions: Array<{
    property: IResearchEntityRef;
    disposition: ResearchIntakeExclusionDispositionEnum;
    sourceClient: IResearchEntityRef | null;
  }>;
  subjectAdvisories: Array<{
    property: IResearchEntityRef;
    workItemId: string;
    stage: ResearchStageEnum;
  }>;
}

export interface IResearchIntakeResponse {
  id: string;
  results: Array<{
    property: IResearchEntityRef;
    workItemId: string | null;
    stage: ResearchStageEnum | null;
    disposition: string;
  }>;
}

export interface IResearchVersionRequest {
  version: number;
}

export interface IResearchUnclaimRequest extends IResearchVersionRequest {
  reason: string;
}

export interface IResearchTransitionRequest extends IResearchVersionRequest {
  action: ResearchAction;
  reason?: string;
}

export interface IResearchAssignmentRequest extends IResearchVersionRequest {
  userId: string | null;
  reason?: string;
}

export interface IResearchNoteRequest {
  note: string;
}

export interface IResearchRequiresFixRequest {
  workItemVersion: number;
  reviewNotes: string;
}

export interface IResearchCompContext {
  workItemId: string;
  version: number;
  compId?: string;
  mode: "choose" | "create" | "edit" | "view";
}

export interface IResearchEditDemandRequest {
  version: number;
  dueDate: string;
  workType: ResearchWorkTypeEnum;
}
